import { useState, useEffect } from 'react';
import { Image, X, ChevronLeft, ChevronRight, ExternalLink, Camera, MapPin, Building2, Sparkles } from 'lucide-react';

/**
 * PhotoReveal - Displays grounded photos in chat context
 * 
 * Visual Truthfulness principles:
 * - Clear labels distinguish provider/area/experience photos
 * - Never ambiguous about what type of photo is shown
 * - Fallback to area is transparent but framed positively
 */

// Get display label based on entity type
const getEntityLabel = (entityType, entityName, fallbackUsed) => {
  if (fallbackUsed) {
    return {
      label: 'Atmosfera della zona',
      sublabel: entityName,
      icon: MapPin,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    };
  }
  
  switch (entityType) {
    case 'provider':
      return {
        label: 'La struttura',
        sublabel: entityName,
        icon: Building2,
        color: 'text-umbria-olive',
        bgColor: 'bg-umbria-olive/10'
      };
    case 'area':
      return {
        label: 'Atmosfera della zona',
        sublabel: entityName,
        icon: MapPin,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50'
      };
    case 'town':
      return {
        label: 'Il borgo',
        sublabel: entityName,
        icon: MapPin,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50'
      };
    case 'experience':
      return {
        label: "L'esperienza",
        sublabel: entityName,
        icon: Sparkles,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50'
      };
    default:
      return {
        label: entityName,
        sublabel: '',
        icon: Image,
        color: 'text-gray-600',
        bgColor: 'bg-gray-100'
      };
  }
};

export const PhotoReveal = ({ 
  photos = [], 
  entityName = '',
  entityType = 'provider',  // 'provider' | 'area' | 'town' | 'experience'
  fallbackUsed = false,
  onClose,
  isCompact = false
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  if (!photos || photos.length === 0) return null;
  
  const currentPhoto = photos[currentIndex];
  const labelInfo = getEntityLabel(entityType, entityName, fallbackUsed);
  const IconComponent = labelInfo.icon;
  
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
    setImageLoaded(false);
  };
  
  const handleNext = () => {
    setCurrentIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
    setImageLoaded(false);
  };
  
  // Compact inline version (for chat bubble)
  if (isCompact) {
    return (
      <div 
        className="mt-3 rounded-xl overflow-hidden bg-umbria-stone/5 border border-umbria-stone/20"
        data-testid="photo-reveal-compact"
      >
        {/* Clear type label header */}
        <div className={`px-3 py-1.5 flex items-center gap-2 ${labelInfo.bgColor} border-b border-umbria-stone/10`}>
          <IconComponent size={14} className={labelInfo.color} />
          <span className={`text-xs font-medium ${labelInfo.color}`}>{labelInfo.label}</span>
          {labelInfo.sublabel && (
            <span className="text-xs text-gray-500">• {labelInfo.sublabel}</span>
          )}
        </div>
        
        <div className="relative aspect-video">
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-umbria-stone/20">
              <Camera className="w-8 h-8 text-umbria-stone/50 animate-pulse" />
            </div>
          )}
          <img
            src={currentPhoto.url}
            alt={currentPhoto.caption || entityName}
            className={`w-full h-full object-cover transition-opacity duration-300 cursor-zoom-in ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
            onClick={() => setShowLightbox(true)}
          />
          
          {/* Navigation arrows */}
          {photos.length > 1 && (
            <>
              <button
                onClick={handlePrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 
                           flex items-center justify-center text-white hover:bg-black/60 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 
                           flex items-center justify-center text-white hover:bg-black/60 transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}
          
          {/* Photo counter */}
          {photos.length > 1 && (
            <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/40 text-white text-xs">
              {currentIndex + 1} / {photos.length}
            </div>
          )}
        </div>
        
        {/* Caption - enhanced with context */}
        <div className="px-3 py-2">
          {currentPhoto.caption && (
            <p className="text-sm text-gray-700">{currentPhoto.caption}</p>
          )}
        </div>
        
        {/* Lightbox */}
        {showLightbox && (
          <PhotoLightbox
            photos={photos}
            currentIndex={currentIndex}
            entityName={entityName}
            entityType={entityType}
            labelInfo={labelInfo}
            onClose={() => setShowLightbox(false)}
            onNavigate={setCurrentIndex}
          />
        )}
      </div>
    );
  }
  
  // Full panel version (for side panel or modal)
  return (
    <div 
      className="bg-white rounded-2xl shadow-xl overflow-hidden"
      data-testid="photo-reveal-full"
    >
      {/* Header with clear type label */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-umbria-stone/20">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full ${labelInfo.bgColor} flex items-center justify-center`}>
            <IconComponent size={16} className={labelInfo.color} />
          </div>
          <div>
            <p className={`text-xs font-medium ${labelInfo.color}`}>{labelInfo.label}</p>
            <h3 className="font-medium text-umbria-charcoal">{entityName}</h3>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-umbria-stone/20 flex items-center justify-center"
          >
            <X size={18} />
          </button>
        )}
      </div>
      
      {/* Main image */}
      <div className="relative aspect-[4/3]">
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-umbria-stone/30">
            <div className="w-8 h-8 border-2 border-umbria-olive/30 border-t-umbria-olive rounded-full animate-spin" />
          </div>
        )}
        <img
          src={currentPhoto.url}
          alt={currentPhoto.caption || entityName}
          className={`w-full h-full object-cover cursor-zoom-in transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
          onClick={() => setShowLightbox(true)}
        />
        
        {/* Navigation */}
        {photos.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 
                         flex items-center justify-center text-white hover:bg-black/60 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 
                         flex items-center justify-center text-white hover:bg-black/60 transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </div>
      
      {/* Caption and info */}
      <div className="px-4 py-3">
        {currentPhoto.caption && (
          <p className="text-gray-700 text-sm">{currentPhoto.caption}</p>
        )}
        
        {photos.length > 1 && (
          <div className="mt-3 flex gap-1.5 justify-center">
            {photos.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setCurrentIndex(idx);
                  setImageLoaded(false);
                }}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentIndex ? 'bg-umbria-olive' : 'bg-umbria-stone/40'
                }`}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Lightbox */}
      {showLightbox && (
        <PhotoLightbox
          photos={photos}
          currentIndex={currentIndex}
          entityName={entityName}
          onClose={() => setShowLightbox(false)}
          onNavigate={setCurrentIndex}
        />
      )}
    </div>
  );
};

/**
 * PhotoLightbox - Full-screen photo viewer
 */
const PhotoLightbox = ({ photos, currentIndex, entityName, onClose, onNavigate }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const currentPhoto = photos[currentIndex];
  
  const handlePrev = () => {
    onNavigate(currentIndex > 0 ? currentIndex - 1 : photos.length - 1);
    setImageLoaded(false);
  };
  
  const handleNext = () => {
    onNavigate(currentIndex < photos.length - 1 ? currentIndex + 1 : 0);
    setImageLoaded(false);
  };
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeydown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [currentIndex]);
  
  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-fade-in"
      data-testid="photo-lightbox"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 
                   flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
      >
        <X size={22} />
      </button>
      
      {/* Image container */}
      <div 
        className="relative max-w-5xl max-h-[90vh] mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}
        <img
          src={currentPhoto.url}
          alt={currentPhoto.caption || entityName}
          className={`max-h-[85vh] rounded-lg transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
        />
        
        {/* Navigation */}
        {photos.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 
                         flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 
                         flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>
      
      {/* Caption and counter */}
      <div className="absolute bottom-6 left-0 right-0 text-center px-4">
        {currentPhoto.caption && (
          <p className="text-white/80 text-sm mb-2">{currentPhoto.caption}</p>
        )}
        {photos.length > 1 && (
          <p className="text-white/60 text-xs">
            {currentIndex + 1} / {photos.length}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * PhotoTriggerButton - Button to show photos (for chat actions)
 */
export const PhotoTriggerButton = ({ 
  entityType,
  entityId,
  entityName,
  label = 'Vedi foto',
  onShowPhotos
}) => {
  const [loading, setLoading] = useState(false);
  
  const handleClick = async () => {
    setLoading(true);
    try {
      if (onShowPhotos) {
        await onShowPhotos({ entityType, entityId, entityName });
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-umbria-olive/10 
                 hover:bg-umbria-olive/20 text-umbria-olive rounded-full transition-colors
                 border border-umbria-olive/20 disabled:opacity-50"
      data-testid={`photo-trigger-${entityId}`}
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-umbria-olive/30 border-t-umbria-olive rounded-full animate-spin" />
      ) : (
        <Camera size={14} />
      )}
      {label}
    </button>
  );
};

export default PhotoReveal;
